import React from 'react';
import { Box, Button, Container, Typography } from '@material-ui/core';

const renderBackgroundLayers = (layers = []) => {
    if (!Array.isArray(layers) || !layers.length) {
        return null;
    }

    return layers.map((layer, index) => (
        <Box
            key={`cta-bg-layer-${index}`}
            className={layer.className || undefined}
            style={layer.styles || {}}
        />
    ));
};

const CtaDualButton = ({ sectionConfig = {}, layoutContext = {} }) => {
    const { handleLoginInitiated, handleDownloadWhitepaper, functionMap = {}, getIcon, getAnimationProps } = layoutContext;

    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );

    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const containerAnimation = applyAnimation(sectionConfig.animation?.container);
    const buttonGroupAnimation = applyAnimation(sectionConfig.buttonGroup?.animation);

    const hasBackgroundLayers = Array.isArray(sectionConfig.backgroundLayers) && sectionConfig.backgroundLayers.length > 0;
    const ctaStyles = { ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) };
    if (hasBackgroundLayers) {
        ctaStyles.position = ctaStyles.position || 'relative';
        ctaStyles.overflow = ctaStyles.overflow || 'hidden';
    }

    const buttons = sectionConfig.buttons || [];

    const handleButtonClick = (button) => {
        // If button has href, open in new tab
        if (button.href || button.url) {
            window.open(button.href || button.url, button.target || '_blank', 'noopener');
            return;
        }
        if (button.action && typeof functionMap[button.action] === 'function') {
            functionMap[button.action](button.actionPayload);
            return;
        }
        // Fallback actions based on button text
        if (button.text?.toLowerCase().includes('consultation') || button.text?.toLowerCase().includes('demo')) {
            handleLoginInitiated?.();
        } else if (button.text?.toLowerCase().includes('whitepaper') || button.text?.toLowerCase().includes('download')) {
            handleDownloadWhitepaper?.();
        }
    };

    return (
        <Box
            id="cta-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={ctaStyles}
        >
            {hasBackgroundLayers && renderBackgroundLayers(sectionConfig.backgroundLayers)}
            <Container
                maxWidth={sectionConfig.container?.maxWidth || 'md'}
                className={containerAnimation.className}
                data-anim={containerAnimation.dataAnim}
                data-delay={containerAnimation.dataDelay}
                data-trigger-once={containerAnimation.dataTriggerOnce}
                style={{
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 10,
                    ...(sectionConfig.container?.styles || {}),
                    ...(containerAnimation.style || {}),
                    ...(sectionConfig.contentStyles || {})
                }}
            >
                <Typography variant={sectionConfig.typography?.title?.variant || 'h3'} style={sectionConfig.titleStyles || {}}>
                    {sectionConfig.title}
                </Typography>
                {sectionConfig.subtitle && (
                    <Typography variant={sectionConfig.typography?.subtitle?.variant || 'body1'} style={sectionConfig.subtitleStyles || {}}>
                        {sectionConfig.subtitle}
                    </Typography>
                )}
                <Box
                    className={buttonGroupAnimation.className}
                    data-anim={buttonGroupAnimation.dataAnim}
                    data-delay={buttonGroupAnimation.dataDelay}
                    data-trigger-once={buttonGroupAnimation.dataTriggerOnce}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: sectionConfig.buttonGroup?.gap || 16,
                        flexWrap: 'wrap',
                        ...(sectionConfig.buttonGroup?.styles || {}),
                        ...(buttonGroupAnimation.style || {})
                    }}
                >
                    {buttons.map((button, index) => (
                        <Button
                            key={`cta-button-${index}`}
                            variant={button.variant || 'contained'}
                            onClick={() => handleButtonClick(button)}
                            style={button.styles || {}}
                            endIcon={button.icon && getIcon ? getIcon(button.icon) : undefined}
                        >
                            {button.text || button.label}
                        </Button>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default CtaDualButton;
